<template>
  <div class="space-y-4">
    <!-- Summary Stats -->
    <div class="bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden">
      <div class="flex items-center gap-2 mb-6">
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {{ t('app.views.cashRegister.financialSummary') }}
        </h3>
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard
          :label="t('app.views.cashRegister.totalSales')"
          :value="formatCurrency(sessionSummary?.total_sales || 0)"
          icon-path="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          icon-bg-class="bg-green-50 dark:bg-green-900/20"
          icon-color-class="text-green-600 dark:text-green-400"
        />
        <StatCard
          :label="t('app.views.cashRegister.totalExpenses')"
          :value="formatCurrency(sessionSummary?.total_expenses || 0)"
          icon-path="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
          icon-bg-class="bg-red-50 dark:bg-red-900/20"
          icon-color-class="text-red-600 dark:text-red-400"
        />
        <StatCard
          :label="t('app.views.cashRegister.totalTransactions')"
          :value="sessionSummary?.total_transactions || 0"
          icon-path="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          icon-bg-class="bg-blue-50 dark:bg-blue-900/20"
          icon-color-class="text-blue-600 dark:text-blue-400"
        />
      </div>
    </div>

    <!-- Payment Breakdown -->
    <PaymentBreakdownSection
      v-if="sessionSummary?.payment_breakdown"
      :payment-breakdown="sessionSummary.payment_breakdown"
    />

    <!-- Balance Comparison (for closed sessions) -->
    <div
      v-if="session?.status === 'closed' && sessionSummary"
      class="bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden"
    >
      <div class="flex items-center gap-2 mb-6">
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {{ t('app.views.cashRegister.balanceComparison') }}
        </h3>
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      </div>
      <BalanceComparisonSection
        :expected-balance="lastCut?.expected_final_balance || 0"
        :actual-balance="lastCut?.actual_final_balance || 0"
        :difference-class="getDifferenceClass(lastCut?.actual_final_balance, lastCut?.expected_final_balance)"
        :difference-text-class="getDifferenceTextClass(lastCut?.actual_final_balance, lastCut?.expected_final_balance)"
        :difference-label="getDifferenceLabel(lastCut?.actual_final_balance, lastCut?.expected_final_balance)"
        :formatted-difference="formatDifference(lastCut?.actual_final_balance, lastCut?.expected_final_balance)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/priceHelpers';
import type { Session, SessionSummary, SessionCut } from './sessionDetailsTypes';
import StatCard from './StatCard.vue';
import PaymentBreakdownSection from './PaymentBreakdownSection.vue';
import BalanceComparisonSection from './BalanceComparisonSection.vue';
import { useSessionDetailsSummary } from '@/composables/useSessionDetailsSummary';

interface Props {
  session: Session | null;
  sessionSummary: SessionSummary | null;
  lastCut: SessionCut | null;
}

const props = defineProps<Props>();
const { t } = useI18n();

const {
  getDifferenceClass,
  getDifferenceTextClass,
  getDifferenceLabel,
  formatDifference,
} = useSessionDetailsSummary();
</script>
